import React, { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useSearch } from '@/hooks/useSearch';
import { highlightText, stripHtml } from '@/modules/Search/utils';
import { cx } from '@/utils';
import { VersionStates } from '@common/Providers/GraphqlProvider/generated';

import { DynamicContent, StaticRoute } from '@/utils/searchHelper';
import { StateTag } from '@/components/common/StateTag';

import SearchInput from '../../../../../components/common/SearchInput/SearchInput';

/**
 * SearchInputWithResults Component
 *
 * Maneja la navegación inteligente:
 * - Contenido estático: navega directamente a la página
 * - Contenido dinámico: navega al listado con filtro por título
 * - Ejemplo: /admin/home/content-management/documents?filter_title=Mi Documento
 * - La tabla detecta el parámetro 'filter_title' y aplica el filtro automáticamente
 *
 * Esta lógica mejora la experiencia permitiendo ir directamente al contenido filtrado
 */

// Local labels for content types
const CONTENT_TYPE_LABELS: Record<string, string> = {
  news: 'Noticias',
  event: 'Eventos',
  events: 'Eventos',
  regulation: 'Normativas',
  regulations: 'Normativas',
  procedure: 'Trámites',
  procedures: 'Trámites',
  document: 'Documentos',
  documents: 'Documentos',
  program: 'Programas',
  programs: 'Programas',
  page: 'Páginas',
  pages: 'Páginas',
  dynamic_page: 'Páginas Dinámicas',
  dynamic_pages: 'Páginas Dinámicas',
  pqrsd: 'PQRSD',
  pqr: 'PQRSD',
  action: 'Acciones',
  institutional_program: 'Programas Institucionales',
  institutional_programs: 'Programas Institucionales',
  microsite: 'Micrositios',
  micro_site: 'Micrositios',
  site: 'Sitios',
  // Agregamos variaciones adicionales
  new: 'Noticias', // Mapeo para el tipo "new" del backend
  noticia: 'Noticia',
  evento: 'Evento',
  normativa: 'Normativa',
  tramite: 'Trámite',
  programa: 'Programa',
  pagina: 'Página',
};

// Helper function to get status badge for content items
const getStatusBadge = (status?: VersionStates, category?: string) => {
  // Solo mostrar badge para contenido de la categoría "Contenido"
  if (category !== 'Contenido' || !status) return null;

  return <StateTag status={status} variant="compact" className="ml-2 flex-shrink-0" />;
};

interface AdminSearchProps {
  placeholder?: string;
  className?: string;
  containerClass?: string;
  iconColor?: string;
  onResultSelect?: (result: any) => void;
  maxStaticResults?: number;
  maxDynamicResults?: number;
  showCategories?: boolean;
  autoNavigate?: boolean; // Por defecto será false
  onInputBlur?: () => void; // Callback para cuando el input pierde el focus
  inputRef?: RefObject<HTMLInputElement>;
}

const AdminSearch: React.FC<AdminSearchProps> = ({
  placeholder = 'Buscar páginas y contenido...',
  className,
  containerClass,
  iconColor,
  onResultSelect,
  maxStaticResults = 5,
  maxDynamicResults = 5,
  showCategories = true,
  autoNavigate = false, // Cambiado a false por defecto
  onInputBlur,
  inputRef,
}) => {
  const searchRef = useRef<HTMLDivElement>(null);

  const {
    searchTerm,
    setSearchTerm,
    results,
    isLoading,
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex,
    totalResults,
    handleResultSelect,
    handleKeyNavigation,
    clearSearch,
  } = useSearch({
    maxStaticResults,
    maxDynamicResults,
    autoNavigate: autoNavigate ?? false, // No navegar automáticamente por defecto
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  const handleInputChange = (value: string): void => {
    setSearchTerm(value);
  };

  const handleSubmit = () => {
    if (selectedIndex >= 0 && totalResults > 0) {
      // Submit selected result
      if (selectedIndex < results.static.length) {
        handleResultSelect(results.static[selectedIndex], 'static');
      } else {
        const dynamicIndex = selectedIndex - results.static.length;
        handleResultSelect(results.dynamic[dynamicIndex], 'dynamic');
      }
    }
    // Removido: navegación automática a página de búsqueda
  };

  const handleDelete = () => {
    clearSearch();
  };

  const handleResultClick = (result: StaticRoute | DynamicContent, type: 'static' | 'dynamic') => {
    const selectedResult = handleResultSelect(result, type);
    if (onResultSelect) {
      onResultSelect(selectedResult);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) {
      handleKeyNavigation(e);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  };

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative w-full">
        <SearchInput
          value={searchTerm}
          placeholder={placeholder}
          className={className}
          containerClass={containerClass}
          iconColor={iconColor}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          onBlur={onInputBlur}
          ref={inputRef}
        />

        {/* Results Dropdown */}
        {isOpen && searchTerm.length > 0 && (
          <div className="scrollbar-hide absolute right-0 top-full z-50 max-h-[438px] w-[620px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
            {isLoading ? (
              <div className="p-4 text-center">
                <div className="animate-pulse">Buscando...</div>
              </div>
            ) : totalResults > 0 ? (
              <>
                {/* Static Results - Grouped by Category */}
                {results.static.length > 0 &&
                  (() => {
                    // Group static results by category
                    const groupedStaticResults = results.static.reduce(
                      (acc, result) => {
                        const category = result.category || 'Otros';
                        if (!acc[category]) {
                          acc[category] = [];
                        }
                        acc[category].push(result);
                        return acc;
                      },
                      {} as Record<string, typeof results.static>
                    );

                    return Object.entries(groupedStaticResults).map(
                      ([category, categoryResults]) => (
                        <div key={`static-${category}`}>
                          {showCategories && (
                            <div className="border-b bg-gray-50 px-4 py-2 text-sm font-semibold uppercase text-marine">
                              {category}
                            </div>
                          )}
                          {categoryResults.map((result, index) => (
                            <button
                              key={`static-${category}-${index}`}
                              className={cx(
                                'w-full border-b border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50',
                                {
                                  'border-blue-200 bg-blue-50':
                                    selectedIndex === results.static.indexOf(result),
                                }
                              )}
                              onClick={() => handleResultClick(result, 'static')}
                            >
                              <div className="font-medium text-gray-900">
                                {highlightText(result.label, searchTerm)}
                              </div>
                              {/* <div className="truncate text-sm text-gray-500">{result.route}</div> */}
                              {result.keywords && result.keywords.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {result.keywords
                                    .slice(0, 3)
                                    .map((keyword: string, tagIndex: number) => (
                                      <span
                                        key={tagIndex}
                                        className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800"
                                      >
                                        {keyword}
                                      </span>
                                    ))}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )
                    );
                  })()}

                {/* Dynamic Results - Grouped by Category and Type */}
                {results.dynamic.length > 0 &&
                  (() => {
                    // Group results by category first, then by content type
                    const groupedResults = results.dynamic.reduce(
                      (acc, result) => {
                        const category = result.category || 'Administrador de Contenido';
                        const contentType =
                          CONTENT_TYPE_LABELS[result.type?.toLowerCase()] ||
                          CONTENT_TYPE_LABELS[result.type] ||
                          result.type ||
                          'Otros';

                        if (!acc[category]) {
                          acc[category] = {};
                        }
                        if (!acc[category][contentType]) {
                          acc[category][contentType] = [];
                        }
                        acc[category][contentType].push(result);
                        return acc;
                      },
                      {} as Record<string, Record<string, typeof results.dynamic>>
                    );

                    return Object.entries(groupedResults).map(([category, typeGroups]) => (
                      <div key={category}>
                        {showCategories && (
                          <div className="border-b bg-gray-50 px-4 py-2 text-sm font-semibold uppercase text-marine">
                            {category}
                          </div>
                        )}
                        {Object.entries(typeGroups).map(([contentType, contentResults]) => (
                          <div key={`${category}-${contentType}`}>
                            {/* Subcategory header for content type */}
                            <div className="bg-gray-25 my-1  px-6 py-2 text-xs font-medium text-gray-600">
                              {contentType}
                            </div>
                            {contentResults.map((result, index) => {
                              const resultIndex =
                                results.static.length + results.dynamic.indexOf(result);
                              return (
                                <button
                                  key={`dynamic-${category}-${contentType}-${index}`}
                                  className={cx(
                                    'w-full px-4 py-3 pl-8 text-left transition-colors hover:bg-gray-50',
                                    {
                                      'border-blue-200 bg-blue-50': selectedIndex === resultIndex,
                                    }
                                  )}
                                  onClick={() => handleResultClick(result, 'dynamic')}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="mr-2 flex-1 font-medium text-gray-900">
                                      {highlightText(result.title, searchTerm)}
                                    </div>
                                    {getStatusBadge(result.status, result.category)}
                                  </div>
                                  <div className="truncate text-sm text-gray-500">
                                    {highlightText(
                                      stripHtml(result.description || '', searchTerm),
                                      searchTerm
                                    )}
                                  </div>
                                  {/* <div className="truncate text-sm text-gray-500">{result.url}</div> */}
                                </button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ));
                  })()}
              </>
            ) : (
              <div className="p-4 text-center text-gray-500">
                <div className="mb-2">No se encontraron resultados</div>
                <div className="text-sm">Intenta con otras palabras clave</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSearch;
